import { useEffect, useState } from 'preact/hooks';
import type { MutableRef } from 'preact/hooks';
import { colors, alpha } from '@pinmerge/tokens';
import type { LocalComment } from '../storage';
import type { Active } from '../lib/types';
import { resolveCommentElement } from '../lib/pin-geometry';
import { isOwnComment } from '../lib/comments';

interface UseMarkersArgs {
  comments: LocalComment[];
  active: Active;
  ownTokenRef: MutableRef<string>;
}

export interface UseMarkersResult {
  scroll: { x: number; y: number };
  hoveredId: string | null;
  setHoveredId: (id: string | null) => void;
}

export function useMarkers({ comments, active, ownTokenRef }: UseMarkersArgs): UseMarkersResult {
  const [scroll, setScroll] = useState({ x: 0, y: 0 });
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  // Layout tick forces a re-render on resize/ResizeObserver fires so element-
  // anchored pins recompute their positions.
  const [, setLayoutTick] = useState(0);

  // visualViewport tracking — writes the *visible* viewport height into a CSS
  // variable on documentElement so the mobile full-screen pin overlay
  // (styles.ts @media max-width:640px) shrinks when the on-screen keyboard
  // pushes up. Without this, position:fixed elements anchor to the layout
  // viewport and the composer ends up hidden behind the keyboard.
  // Custom properties inherit through Shadow DOM, so setting on the document
  // root reaches the pin via var(--pinmerge-vv-h, 100dvh).
  useEffect(() => {
    const update = () => {
      const h = window.visualViewport?.height ?? window.innerHeight;
      document.documentElement.style.setProperty('--pinmerge-vv-h', `${h}px`);
    };
    update();
    const vv = window.visualViewport;
    vv?.addEventListener('resize', update);
    vv?.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => {
      vv?.removeEventListener('resize', update);
      vv?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  // Track scroll + layout shifts so markers and the active pin stay anchored.
  // Element-relative pin positions need a recompute whenever the host page's
  // layout changes — async image load, font swap, responsive breakpoint, etc.
  // ResizeObserver on <body> catches all of that without polling.
  useEffect(() => {
    const onScroll = () => setScroll({ x: window.scrollX, y: window.scrollY });
    const bumpLayout = () => {
      setScroll({ x: window.scrollX, y: window.scrollY });
      setLayoutTick((t) => t + 1);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', bumpLayout);
    const obs = new ResizeObserver(bumpLayout);
    obs.observe(document.body);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', bumpLayout);
      obs.disconnect();
    };
  }, []);

  // Highlight the original element when a marker is hovered or its pin is
  // open. Falls back gracefully if the selector no longer matches anything in
  // the DOM.
  useEffect(() => {
    const highlightId = hoveredId ?? (active?.kind === 'view' ? active.id : null);
    if (!highlightId) return;
    const c = comments.find((x) => x.id === highlightId);
    if (!c || !c.elementSelector) return;
    const el = resolveCommentElement(c);
    if (!el) return;

    const box = document.createElement('div');
    box.setAttribute('data-pinmerge-hover', 'box');
    const isMine = isOwnComment(c, ownTokenRef.current);
    const colour = isMine ? colors.pin.own : colors.pin.other;
    const bg = isMine ? alpha(colors.pin.own, 0.08) : alpha(colors.pin.other, 0.1);
    box.style.cssText = [
      'position: fixed',
      'pointer-events: none',
      'z-index: 2147483639',
      `border: 2px solid ${colour}`,
      `background: ${bg}`,
      'border-radius: 4px',
      'box-sizing: border-box',
      'transition: all 80ms linear',
      'margin: 0',
      'padding: 0',
    ].join(';');
    document.body.appendChild(box);

    // Coalesce scroll/resize writes into one rAF so a burst of events paints
    // the box once per frame instead of per event.
    let rafId = 0;
    const apply = () => {
      rafId = 0;
      const r = el.getBoundingClientRect();
      box.style.left = `${r.left}px`;
      box.style.top = `${r.top}px`;
      box.style.width = `${r.width}px`;
      box.style.height = `${r.height}px`;
    };
    const sync = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(apply);
    };
    apply(); // initial position, synchronous
    window.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    return () => {
      window.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
      if (rafId) cancelAnimationFrame(rafId);
      box.remove();
    };
  }, [hoveredId, active, comments]);

  return { scroll, hoveredId, setHoveredId };
}
